import type { PrioridadeTriagem } from '../didatico/ocupantes'
import {
  PRESET_CENARIO_1_COLISAO_MURO,
  PRESET_CENARIO_2_COLISAO_LATERAL,
  PRESET_CENARIO_3_CAPOTAMENTO,
  PRESET_CENARIO_4_CARRO_SOBRE_OUTRO,
  PRESET_CENARIO_5_DUAS_ESTAVEIS,
  PRESET_CENARIO_6_ATROPELAMENTO,
  PRESET_CENARIO_7_BARREIRA_PROTECAO,
} from './preset'
import type { CenaTabletop } from './tipos'
import { copiarCenaTabletop } from './operacoes'

export type IdEtapaAula =
  | '360'
  | 'riscos'
  | 'cinematica'
  | 'abordagem'
  | 'triagem_xabcde'
  | 'extracao'

export type CategoriaCartaoDidatico =
  | 'cena'
  | 'seguranca'
  | 'risco'
  | 'cinematica'
  | 'clinico'
  | 'conduta'
  | 'extracao'

export interface CartaoDidaticoRevelavel {
  readonly id: string
  readonly titulo: string
  readonly subtitulo?: string
  readonly categoria: CategoriaCartaoDidatico
  readonly destaque?: string
  readonly conteudo: string
  readonly detalhes?: readonly string[]
  readonly condutaRecomendada?: string
  readonly vitimaId?: string
}

export interface EtapaDidaticaAula {
  readonly id: IdEtapaAula
  readonly numero: number
  readonly nome: string
  readonly subtitulo: string
  readonly decisaoTatica: string
  readonly diretrizOperacional: string
  /** @deprecated use decisaoTatica */
  readonly perguntaTurma?: string
  /** @deprecated use diretrizOperacional */
  readonly orientacaoInstrutor?: string
  readonly focoCamera: 'panoramica' | 'riscos' | 'cinematica' | 'vitima' | 'extracao'
  readonly poseCamera?: readonly [number, number, number]
  readonly cartoes: readonly CartaoDidaticoRevelavel[]
}

export interface ParametrosXabcdeVitima {
  readonly xHemorragia: { readonly valor: string; readonly critico: boolean }
  readonly aViasAereas: { readonly valor: string; readonly conduta: string }
  readonly bRespiracao: { readonly fr: string; readonly spo2: string; readonly padrao: string }
  readonly cCirculacao: { readonly fc: string; readonly pa: string; readonly tec: string; readonly estado: string }
  readonly dNeurologico: { readonly glasgow: string; readonly pupilas: string; readonly avdi: string }
  readonly eExposicao: { readonly temp: string; readonly achados: string }
}

export interface VitimaResumoCenario {
  readonly id: string
  readonly nomeCurto: string
  readonly papel: string
  readonly triagemReferencia: PrioridadeTriagem
  readonly local: 'solo' | 'veiculo'
  readonly status: 'critica' | 'estavel' | 'potencial'
  readonly parametrosXabcde?: ParametrosXabcdeVitima
}

export interface CenarioDidatico {
  readonly id: string
  readonly idPreset: string
  readonly titulo: string
  readonly subtitulo: string
  readonly descricao: string
  readonly objetivoOperacional: string
  /** @deprecated use objetivoOperacional */
  readonly objetivoAula?: string
  readonly badge: string
  readonly vitimas: readonly VitimaResumoCenario[]
  readonly criarCena: () => CenaTabletop
  readonly etapas: readonly EtapaDidaticaAula[]
}

/**
 * Cenário 1: Colisão Frontal contra Muro
 */
const CENARIO_1_COLISAO_MURO: CenarioDidatico = {
  id: 'cenario-1',
  idPreset: 'cenario-1',
  titulo: 'Cenário 1: Colisão Frontal contra Muro',
  subtitulo: 'Condutor Retido em Choque Hipovolêmico (Vítima Crítica)',
  descricao: 'Sedã colidido contra muro de concreto rígido. Condutor retido pelo painel e pedaleira com choque hipovolêmico grave.',
  objetivoOperacional: 'Identificar choque hipovolêmico descompensado no XABCDE, gerenciar riscos de combustível/muro e coordenar extração rápida com alívio de painel.',
  objetivoAula: 'Identificar choque hipovolêmico descompensado no XABCDE, gerenciar riscos de combustível/muro e coordenar extração rápida com alívio de painel.',
  badge: '1 Vítima (Crítica / Choque)',
  vitimas: [
    {
      id: 'slide_49_v1_motorista',
      nomeCurto: 'V1 — Motorista',
      papel: 'Condutor',
      triagemReferencia: 'vermelho',
      local: 'veiculo',
      status: 'critica',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem sangramento externo ativo de grandes vasos', critico: false },
        aViasAereas: { valor: 'Pérvia com fala curta/entrecortada', conduta: 'Estabilização manual da cervical e colar cervical' },
        bRespiracao: { fr: '28 irpm (Taquipneia)', spo2: '91% (Hipoxemia)', padrao: 'Respiração superficial e reduzida' },
        cCirculacao: { fc: '124 bpm (Taquicardia)', pa: '85/55 mmHg (Hipotensão)', tec: '> 3 s (Lentificado)', estado: 'Pele fria, pálida e sudoreica (Choque Descompensado)' },
        dNeurologico: { glasgow: 'Glasgow 10 (Confuso / Sonolento)', pupilas: 'Isocóricas e fotorreagentes', avdi: 'Reage a estímulo Verbal/Dor' },
        eExposicao: { temp: '35,4 °C (Hipotermia em evolução)', achados: 'Pernas presas sob pedaleira; manta térmica indicada' },
      },
    },
  ],
  criarCena: () => copiarCenaTabletop(PRESET_CENARIO_1_COLISAO_MURO),
  etapas: [
    {
      id: '360',
      numero: 1,
      nome: 'Volta 360°',
      subtitulo: 'Reconhecimento & Dimensionamento da Cena',
      decisaoTatica: 'Ao descer da viatura a 15 metros da colisão frontal contra anteparo rígido, qual a primeira ação prioritária da guarnição?',
      diretrizOperacional: 'A guarnição deve posicionar a viatura em ângulo de 45° a 15 metros bloqueando o fluxo viário, delimitar o cone de transição com sinalização e inspecionar a solidez do muro de concreto e o entorno em busca de ejetados.',
      perguntaTurma: 'Ao descer da viatura a 15 metros da colisão frontal contra anteparo rígido, qual a primeira ação prioritária da guarnição?',
      orientacaoInstrutor: 'A guarnição deve posicionar a viatura em ângulo de 45° a 15 metros bloqueando o fluxo viário, delimitar o cone de transição com sinalização e inspecionar a solidez do muro de concreto e o entorno em busca de ejetados.',
      focoCamera: 'panoramica',
      poseCamera: [0, 6, 8],
      cartoes: [
        {
          id: 'c1_360_posicionamento',
          titulo: 'Posicionamento da Viatura',
          categoria: 'seguranca',
          destaque: 'Ângulo de 45° a 15 metros',
          conteudo: 'A viatura deve ser posicionada em ângulo de proteção, criando uma barreira física contra o tráfego da via e garantindo rota de fuga para a equipe.',
          detalhes: [
            'Luzes de emergência ligadas para alerta precoce',
            'Isolamento inicial da pista com cones em cone de transição',
            'Manter espaço livre para chegada do suporte avançado (USA)',
          ],
        },
        {
          id: 'c1_360_veiculo',
          titulo: 'Veículo & Obstáculo Fixo',
          categoria: 'cena',
          destaque: 'Sedã sobre 4 rodas contra muro de concreto',
          conteudo: 'Sedã médio com impacto frontal violento contra barreira indeformável. Não há deformação traseira; habitáculo preservado exceto na coluna A e painel.',
        },
      ],
    },
    {
      id: 'riscos',
      numero: 2,
      nome: 'Riscos da Cena',
      subtitulo: 'Reconhecimento & Controle de Ameaças',
      decisaoTatica: 'Quais perigos imediatos ameaçam a guarnição e a vítima antes de qualquer contato com o veículo colidido?',
      diretrizOperacional: 'Identificar e neutralizar o derramamento de combustível sob o motor armando linha preventiva com extintor PQS/mangueira carregada, aplicar absorvente granular, desligar a bateria e estabilizar o veículo para evitar colapso do muro.',
      perguntaTurma: 'Quais perigos imediatos ameaçam a guarnição e a vítima antes de qualquer contato com o veículo colidido?',
      orientacaoInstrutor: 'Identificar e neutralizar o derramamento de combustível sob o motor armando linha preventiva com extintor PQS/mangueira carregada, aplicar absorvente granular, desligar a bateria e estabilizar o veículo para evitar colapso do muro.',
      focoCamera: 'riscos',
      poseCamera: [-3, 2, 4],
      cartoes: [
        {
          id: 'c1_risco_combustivel',
          titulo: 'Vazamento Ativo de Combustível e Óleo',
          categoria: 'risco',
          destaque: 'Risco Crítico de Incêndio',
          conteudo: 'Líquido inflamável escorrendo do cofre do motor sob o assoalho. Qualquer faísca ou contato com o escapamento quente pode deflagrar incêndio imediato.',
          detalhes: [
            'Linha de proteção pré-conectada com extintor PQS ou mangueira de 1.1/2"',
            'Aplicação imediata de agente absorvente (areia/serragem)',
            'Desconectar a bateria (primeiro o polo negativo) sem produzir centelhas',
          ],
          condutaRecomendada: 'Linha preventiva armada + absorvente espalhado + desligamento elétrico.',
        },
        {
          id: 'c1_risco_vidros_muro',
          titulo: 'Estilhaços de Vidro & Instabilidade do Muro',
          categoria: 'seguranca',
          destaque: 'EPI Completo & Atenção Estrutural',
          conteudo: 'Vidros temperados quebrados ao redor da porta do motorista representam risco de corte e perfuração de mangueiras. O muro trincado pode ceder se for forçado.',
          detalhes: [
            'Uso obrigatório de luvas de resgate e proteção ocular',
            'Calçamento primário das rodas com calços e cunhas',
          ],
        },
      ],
    },
    {
      id: 'cinematica',
      numero: 3,
      nome: 'Cinemática do Trauma',
      subtitulo: 'Biomecânica do Impacto & Padrão de Lesões',
      decisaoTatica: 'Qual a direção da desaceleração e quais lesões biomecânicas internas devem ser antecipadas no condutor?',
      diretrizOperacional: 'Colisão frontal pura contra barreira indeformável transfere toda a energia cinética ao habitáculo. Antecipar cisalhamento aórtico, contusão pulmonar e cardíaca por impacto no volante, fratura de pelve e encarceramento mecânico de membros inferiores nos pedais.',
      perguntaTurma: 'Qual a direção da desaceleração e quais lesões biomecânicas internas devem ser antecipadas no condutor?',
      orientacaoInstrutor: 'Colisão frontal pura contra barreira indeformável transfere toda a energia cinética ao habitáculo. Antecipar cisalhamento aórtico, contusão pulmonar e cardíaca por impacto no volante, fratura de pelve e encarceramento mecânico de membros inferiores nos pedais.',
      focoCamera: 'cinematica',
      poseCamera: [0, 2.5, 4.5],
      cartoes: [
        {
          id: 'c1_cin_mecanismo',
          titulo: 'Trajetória "Por Baixo e Por Cima"',
          categoria: 'cinematica',
          destaque: 'Impacto a ~60 km/h contra barreira rígida',
          conteudo: 'O condutor com cinto sofreu desaceleração abrupta. Os membros inferiores colidiram contra o painel e pedais, e o tórax impactou o cinto e o volante.',
          detalhes: [
            'Fratura/luxação de joelho e fêmur pelo impacto nos pedais',
            'Contusão miocárdica e pulmonar por desaceleração torácica',
            'Laceração hepática ou esplênica por compressão da faixa abdominal do cinto',
          ],
        },
        {
          id: 'c1_cin_painel',
          titulo: 'Colapso do Painel & Volante Deformado',
          categoria: 'cinematica',
          destaque: 'Encarceramento Mecânico Tipo II',
          conteudo: 'A intrusão de 42 cm da dianteira empurrou a coluna de direção e a pedaleira contra as pernas do motorista, impedindo sua movimentação voluntária.',
        },
      ],
    },
    {
      id: 'abordagem',
      numero: 4,
      nome: 'Abordagem da Vítima',
      subtitulo: 'Acesso Inicial & Estabilização Manual',
      decisaoTatica: 'Com a porta dianteira esquerda deformada e emperrada, como realizar a abordagem e contenção da coluna cervical?',
      diretrizOperacional: 'O socorrista 1 deve abordar a vítima frontalmente pelo seu campo visual para impedir rotação cervical, enquanto um socorrista assume a estabilização manual da cabeça a partir do banco traseiro ou vidro quebrado.',
      perguntaTurma: 'Com a porta dianteira esquerda deformada e emperrada, como realizar a abordagem e contenção da coluna cervical?',
      orientacaoInstrutor: 'O socorrista 1 deve abordar a vítima frontalmente pelo seu campo visual para impedir rotação cervical, enquanto um socorrista assume a estabilização manual da cabeça a partir do banco traseiro ou vidro quebrado.',
      focoCamera: 'vitima',
      poseCamera: [-1.8, 1.6, 1.2],
      cartoes: [
        {
          id: 'c1_abo_campo_visao',
          titulo: 'Abordagem pelo Campo de Visão',
          categoria: 'conduta',
          destaque: 'Controle Cervical Manual Obrigatório',
          conteudo: 'O socorrista posiciona-se à frente da linha do olhar do condutor, dizendo: "Bom dia, sou do Corpo de Bombeiros, não mexa o pescoço nem a cabeça".',
          detalhes: [
            'Socorrista 1 estabiliza a cabeça pelas costas ou pelo banco traseiro',
            'Socorrista 2 avalia responsividade e permeabilidade de via aérea',
            'Instalação de colar cervical de tamanho ajustado assim que o eixo for alinhado',
          ],
        },
        {
          id: 'c1_abo_porta_emperrada',
          titulo: 'Porta Dianteira Deformada e Emperrada',
          categoria: 'cena',
          destaque: 'Acesso Secundário ou Ferramenta Combinada',
          conteudo: 'O caixilho da porta do motorista sofreu deformação moderada (22 cm), travando a tranca. O acesso inicial é feito pelo vidro quebrado ou pela porta traseira.',
        },
      ],
    },
    {
      id: 'triagem_xabcde',
      numero: 5,
      nome: 'Protocolo XABCDE',
      subtitulo: 'Avaliação Primária & Parâmetros Críticos',
      decisaoTatica: 'Diante de taquipneia (28 irpm), pulso filiforme, má perfusão e Glasgow 10 no exame primário, quais os parâmetros críticos do XABCDE e a conduta imediata?',
      diretrizOperacional: 'Identificar choque hipovolêmico descompensado no XABCDE (B: 28 irpm, C: PA 85/55 mmHg e FC 124 bpm, D: Glasgow 10). Administrar O2 a 15 L/min sob máscara com reservatório, manter aquecimento com manta térmica e solicitar suporte avançado de vida.',
      perguntaTurma: 'Diante de taquipneia (28 irpm), pulso filiforme, má perfusão e Glasgow 10 no exame primário, quais os parâmetros críticos do XABCDE e a conduta imediata?',
      orientacaoInstrutor: 'Identificar choque hipovolêmico descompensado no XABCDE (B: 28 irpm, C: PA 85/55 mmHg e FC 124 bpm, D: Glasgow 10). Administrar O2 a 15 L/min sob máscara com reservatório, manter aquecimento com manta térmica e solicitar suporte avançado de vida.',
      focoCamera: 'vitima',
      poseCamera: [-1.2, 1.4, 0.8],
      cartoes: [
        {
          id: 'c1_tri_prioridade',
          titulo: 'Prioridade Clínica: Vermelho (Crítico)',
          categoria: 'clinico',
          destaque: 'Choque Hipovolêmico Descompensado',
          conteudo: 'Exame primário confirma vítima crítica: taquipneia (28 irpm), pulso radial filiforme e má perfusão periférica (> 3 s), com rebaixamento neurológico (Glasgow 10).',
          detalhes: [
            'Classificação: VERMELHO CRÍTICO',
            'Indicação de transporte imediato após extração rápida',
            'Acionamento de suporte avançado para intubação e reposição volêmica',
          ],
        },
        {
          id: 'c1_tri_xabcde',
          titulo: 'Parâmetros do Protocolo XABCDE',
          categoria: 'clinico',
          destaque: 'Instabilidade Hemodinâmica e Respiratória',
          conteudo: 'X: sem hemorragia externa ativa. A: via aérea pérvia com colar cervical. B: 28 irpm, SpO2 91%. C: PA 85/55 mmHg, FC 124 bpm, pele fria/sudoreica. D: Glasgow 10 (confuso). E: pernas presas.',
          condutaRecomendada: 'Oxigenoterapia sob máscara com reservatório a 15 L/min + prevenção de hipotermia com manta térmica.',
        },
      ],
    },
    {
      id: 'extracao',
      numero: 6,
      nome: 'Decisão de Extração',
      subtitulo: 'Plano Tático de Desencarceramento',
      decisaoTatica: 'Com a vítima instável e pernas retidas sob a pedaleira deformada, qual técnica de desencarceramento deve ser executada?',
      diretrizOperacional: 'Executar extração rápida de tempo crítico: abertura forçada da porta esquerda com ferramenta hidráulica combinada, corte ou alívio de pedais/painel com cilindro expansor e retirada em prancha longa.',
      perguntaTurma: 'Com a vítima instável e pernas retidas sob a pedaleira deformada, qual técnica de desencarceramento deve ser executada?',
      orientacaoInstrutor: 'Executar extração rápida de tempo crítico: abertura forçada da porta esquerda com ferramenta hidráulica combinada, corte ou alívio de pedais/painel com cilindro expansor e retirada em prancha longa.',
      focoCamera: 'extracao',
      poseCamera: [-2.5, 2.2, 2.0],
      cartoes: [
        {
          id: 'c1_ext_tatica',
          titulo: 'Extração Rápida (Tempo Crítico)',
          categoria: 'extracao',
          destaque: 'Janela de Ouro do Trauma',
          conteudo: 'O paciente instável não tolera remoção complexa de teto de 20 minutos. Exige-se manobra tática de alívio rápido de pedais e retirada em prancha longa.',
          detalhes: [
            'Abertura forçada da porta esquerda com ferramenta hidráulica combinada',
            'Alívio do painel/pedais com cilindro expansor ou afastador',
            'Deslizamento controlado sobre prancha rígida longa com colar cervical e tirante',
          ],
          condutaRecomendada: 'Desencarceramento rápido focado no alívio de membros inferiores e prancha longa.',
        },
      ],
    },
  ],
}

/**
 * Cenário 2: Colisão Lateral entre Dois Veículos (T-Bone)
 */
const CENARIO_2_COLISAO_LATERAL: CenarioDidatico = {
  id: 'cenario-2',
  idPreset: 'cenario-2',
  titulo: 'Cenário 2: Colisão Lateral entre Dois Veículos',
  subtitulo: 'Dilema Tático de Acesso e Priorização Clínica',
  descricao: 'Impacto lateral severo em cruzamento (T-Bone) entre sedã e SUV colisor. V1 motorista estável preso por pedais vs V2 passageira em choque por intrusão da porta lateral.',
  objetivoOperacional: 'Gerenciar cena multiveicular acoplada (T-Bone), priorizar a vítima crítica no acesso lateral obstruído e coordenar desencarceramento sem desestabilizar o segundo veículo.',
  objetivoAula: 'Gerenciar cena multiveicular acoplada (T-Bone), priorizar a vítima crítica no acesso lateral obstruído e coordenar desencarceramento sem desestabilizar o segundo veículo.',
  badge: '2 Vítimas (1 Vermelho, 1 Amarelo)',
  vitimas: [
    {
      id: 'slide_51_v1_motorista',
      nomeCurto: 'V1 — Motorista',
      papel: 'Condutor Sedã',
      triagemReferencia: 'amarelo',
      local: 'veiculo',
      status: 'estavel',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem hemorragia ativa (escoriações leves)', critico: false },
        aViasAereas: { valor: 'Pérvia; fala clara e sem desconforto', conduta: 'Estabilização cervical preventiva' },
        bRespiracao: { fr: '18 irpm (Eupneico)', spo2: '98%', padrao: 'Expansão torácica simétrica normal' },
        cCirculacao: { fc: '84 bpm (Normocárdico)', pa: '125/80 mmHg (Normotenso)', tec: '< 2 s', estado: 'Pele corada, quente e seca' },
        dNeurologico: { glasgow: 'Glasgow 15 (Lúcido e orientado)', pupilas: 'Isocóricas fotorreagentes', avdi: 'Alerta' },
        eExposicao: { temp: '36,5 °C', achados: 'Pernas presas sob pedais (encarceramento mecânico)' },
      },
    },
    {
      id: 'slide_51_v2_passageiro',
      nomeCurto: 'V2 — Passageira',
      papel: 'Passageira Sedã',
      triagemReferencia: 'vermelho',
      local: 'veiculo',
      status: 'critica',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem sangramento externo ativo evidente', critico: false },
        aViasAereas: { valor: 'Risco de queda de língua e obstrução', conduta: 'Abertura manual de via aérea e colar cervical' },
        bRespiracao: { fr: '32 irpm (Taquipneia severa)', spo2: '88% (Hipóxia crítica)', padrao: 'Respiração rápida, curta e assimétrica' },
        cCirculacao: { fc: '138 bpm (Taquicardia severa)', pa: '70/40 mmHg (Choque profundo)', tec: '> 4 s', estado: 'Sem pulso radial; pulso carotídeo filiforme; pele fria' },
        dNeurologico: { glasgow: 'Glasgow 8 (Comatosa / Sem resposta verbal)', pupilas: 'Midríase bilateral lenta', avdi: 'Reage apenas à dor profunda' },
        eExposicao: { temp: '34,9 °C (Hipotermia)', achados: 'Deformação da porta direita colapsada sobre bacia e fêmur' },
      },
    },
  ],
  criarCena: () => copiarCenaTabletop(PRESET_CENARIO_2_COLISAO_LATERAL),
  etapas: [
    {
      id: '360',
      numero: 1,
      nome: 'Volta 360°',
      subtitulo: 'Reconhecimento da Cena Multiveicular',
      decisaoTatica: 'Em colisão lateral T-Bone com veículos acoplados, como o SUV impactante condiciona as rotas de acesso e a segurança?',
      diretrizOperacional: 'A dianteira do SUV bloqueia fisicamente o flanco direito do sedã. A guarnição deve estabelecer isolamento perimetral para duas viaturas e definir rotas de entrada pela lateral oposta ou traseira.',
      perguntaTurma: 'Em colisão lateral T-Bone com veículos acoplados, como o SUV impactante condiciona as rotas de acesso e a segurança?',
      orientacaoInstrutor: 'A dianteira do SUV bloqueia fisicamente o flanco direito do sedã. A guarnição deve estabelecer isolamento perimetral para duas viaturas e definir rotas de entrada pela lateral oposta ou traseira.',
      focoCamera: 'panoramica',
      poseCamera: [0, 7, 9],
      cartoes: [
        {
          id: 'c2_360_acesso',
          titulo: 'Dois Veículos Acoplados (Impacto T-Bone)',
          categoria: 'cena',
          destaque: 'Acesso Leste Totalmente Bloqueado',
          conteudo: 'A dianteira do SUV invadiu o habitáculo do sedã pela lateral direita. A porta da passageira está inacessível por fora enquanto o SUV estiver encostado.',
        },
      ],
    },
    {
      id: 'riscos',
      numero: 2,
      nome: 'Riscos da Cena',
      subtitulo: 'Estabilidade Dupla & Fluidos na Junção',
      decisaoTatica: 'Quais riscos mecânicos e químicos decorrem da junção de dois veículos acoplados após impacto lateral?',
      diretrizOperacional: 'A guarnição deve executar o calçamento solidário dos dois veículos antes de qualquer intervenção, eliminar o risco de vazamento mútuo de fluidos com absorvente e isolar simultaneamente os sistemas elétricos dos dois carros.',
      perguntaTurma: 'Quais riscos mecânicos e químicos decorrem da junção de dois veículos acoplados após impacto lateral?',
      orientacaoInstrutor: 'A guarnição deve executar o calçamento solidário dos dois veículos antes de qualquer intervenção, eliminar o risco de vazamento mútuo de fluidos com absorvente e isolar simultaneamente os sistemas elétricos dos dois carros.',
      focoCamera: 'riscos',
      poseCamera: [2, 2.5, 3],
      cartoes: [
        {
          id: 'c2_risco_fluidos',
          titulo: 'Vazamento Duplo de Fluidos na Junção',
          categoria: 'risco',
          destaque: 'Combustível e Ácido de Bateria',
          conteudo: 'Radiadores e linhas de combustível de ambos os veículos foram rompidos na colisão. O solo entre os veículos está altamente escorregadio e inflamável.',
          condutaRecomendada: 'Aplicação imediata de absorvente granular e desligamento das duas baterias.',
        },
        {
          id: 'c2_risco_estabilidade',
          titulo: 'Instabilidade Mecânica Compartilhada',
          categoria: 'seguranca',
          destaque: 'Calçamento Simpático dos Dois Carros',
          conteudo: 'Se um carro for movimentado sem calçar o outro, pode ocorrer deslocamento súbito e esmagamento adicional da passageira retida.',
          condutaRecomendada: 'Calçar o sedã e o SUV antes de qualquer tentativa de afastamento.',
        },
      ],
    },
    {
      id: 'cinematica',
      numero: 3,
      nome: 'Cinemática do Trauma',
      subtitulo: 'Intrusão Lateral sem Zona de Amortecimento',
      decisaoTatica: 'Por que a intrusão de 36 cm na coluna B e porta direita coloca a passageira em risco iminente de morte?',
      diretrizOperacional: 'Impactos laterais não contam com área de absorção (como cofre ou porta-malas). A força de deformação atinge diretamente a bacia, baço/fígado e gradil costal da vítima lateral, gerando hemorragia intra-abdominal exangue.',
      perguntaTurma: 'Por que a intrusão de 36 cm na coluna B e porta direita coloca a passageira em risco iminente de morte?',
      orientacaoInstrutor: 'Impactos laterais não contam com área de absorção (como cofre ou porta-malas). A força de deformação atinge diretamente a bacia, baço/fígado e gradil costal da vítima lateral, gerando hemorragia intra-abdominal exangue.',
      focoCamera: 'cinematica',
      poseCamera: [1.8, 2, 2.2],
      cartoes: [
        {
          id: 'c2_cin_intrusao',
          titulo: 'Intrusão da Coluna B e Porta Direita',
          categoria: 'cinematica',
          destaque: '36 cm de Intrusão Direta',
          conteudo: 'A força foi transferida diretamente para a bacia, tórax lateral e clavícula da passageira (V2). O motorista (V1) sofreu apenas o efeito da inércia lateral.',
        },
      ],
    },
    {
      id: 'abordagem',
      numero: 4,
      nome: 'Abordagem da Vítima',
      subtitulo: 'Acesso Tático com Porta Obstruída',
      decisaoTatica: 'Como acessar e estabilizar a passageira retida com o SUV prensado contra sua porta?',
      diretrizOperacional: 'A entrada da equipe deve ser realizada pelo lado do motorista ou pelo vigia traseiro quebrado. O socorrista interno posiciona-se no banco de trás para assegurar o alinhamento da coluna cervical de V2 e manter via aérea desobstruída.',
      perguntaTurma: 'Como acessar e estabilizar a passageira retida com o SUV prensado contra sua porta?',
      orientacaoInstrutor: 'A entrada da equipe deve ser realizada pelo lado do motorista ou pelo vigia traseiro quebrado. O socorrista interno posiciona-se no banco de trás para assegurar o alinhamento da coluna cervical de V2 e manter via aérea desobstruída.',
      focoCamera: 'vitima',
      poseCamera: [-0.8, 1.6, 1.2],
      cartoes: [
        {
          id: 'c2_abo_socorrista_interno',
          titulo: 'Entrada do Socorrista Interno',
          categoria: 'conduta',
          destaque: 'Acesso pelo Vidro Traseiro ou Porta Esquerda',
          conteudo: 'Um socorrista adentra o banco traseiro para imobilizar a coluna cervical de V2 e proteger suas vias aéreas enquanto a equipe externa opera as ferramentas.',
        },
      ],
    },
    {
      id: 'triagem_xabcde',
      numero: 5,
      nome: 'Protocolo XABCDE',
      subtitulo: 'Priorização Clínica pelo Protocolo XABCDE',
      decisaoTatica: 'Havendo um condutor lúcido (V1) e uma passageira em choque classe III (V2), qual a definição de prioridades no exame clínico do XABCDE?',
      diretrizOperacional: 'Classificar V2 como prioridade crítica / Vermelho (FC 138 carotídeo, sem pulso radial, Glasgow 8) e V1 como potencialmente estável / Amarelo. A intervenção médica e o planejamento de desencarceramento devem priorizar imediatamente a rota de salvamento de V2.',
      perguntaTurma: 'Havendo um condutor lúcido (V1) e uma passageira em choque classe III (V2), qual a definição de prioridades no exame clínico do XABCDE?',
      orientacaoInstrutor: 'Classificar V2 como prioridade crítica / Vermelho (FC 138 carotídeo, sem pulso radial, Glasgow 8) e V1 como potencialmente estável / Amarelo. A intervenção médica e o planejamento de desencarceramento devem priorizar imediatamente a rota de salvamento de V2.',
      focoCamera: 'vitima',
      poseCamera: [0.5, 1.8, 1.5],
      cartoes: [
        {
          id: 'c2_tri_prioridades',
          titulo: 'Priorização XABCDE: V2 Crítica vs V1 Estável',
          categoria: 'clinico',
          destaque: 'Prioridade Clínica vs Viabilidade Tática',
          conteudo: 'V2 (Passageira): FR 32 irpm, pulso radial ausente, FC 138 carotídeo, Glasgow 8 -> Vermelho (Crítica). V1 (Motorista): FR 20 irpm, pulso radial cheio, Glasgow 15 -> Amarelo (Estável).',
          condutaRecomendada: 'Foco total da equipe médica em V2 enquanto a equipe de salvamento desobstrui a rota.',
        },
      ],
    },
    {
      id: 'extracao',
      numero: 6,
      nome: 'Decisão de Extração',
      subtitulo: 'Afastamento do SUV ou Remoção de Teto',
      decisaoTatica: 'Qual manobra de desencarceramento proporciona a rota mais rápida e segura para retirar a passageira crítica?',
      diretrizOperacional: 'Executar a remoção total do teto (técnica do conversível) cortando as colunas e rebatendo para a lateral livre, permitindo a extração vertical direta de V2 em prancha rígida sem movimentar o SUV colisor.',
      perguntaTurma: 'Qual manobra de desencarceramento proporciona a rota mais rápida e segura para retirar a passageira crítica?',
      orientacaoInstrutor: 'Executar a remoção total do teto (técnica do conversível) cortando as colunas e rebatendo para a lateral livre, permitindo a extração vertical direta de V2 em prancha rígida sem movimentar o SUV colisor.',
      focoCamera: 'extracao',
      poseCamera: [0, 4, 4],
      cartoes: [
        {
          id: 'c2_ext_teto',
          titulo: 'Remoção Total do Teto (Conversível)',
          categoria: 'extracao',
          destaque: 'Criação de Amplo Espaço Superior',
          conteudo: 'Ao cortar as colunas e retirar o teto, elimina-se o confinamento e permite-se retirar V2 verticalmente com prancha longa ou plano rígido, sem tocar no SUV.',
        },
      ],
    },
  ],
}

/**
 * Cenário 3: Capotamento com Vítima Ejetada
 */
const CENARIO_3_CAPOTAMENTO: CenarioDidatico = {
  id: 'cenario-3',
  idPreset: 'cenario-3',
  titulo: 'Cenário 3: Capotamento com Vítima Ejetada',
  subtitulo: 'Vítima Ejetada Crítica no Solo e Ocupante Retida Invertida',
  descricao: 'Hatch capotado com teto colapsado e colisão contra defensa. V1 ejetada a 3,4 m com TCE severo (Vítima Crítica); V2 no interior suspensa pelo cinto (Vítima Estável).',
  objetivoOperacional: 'Executar busca ativa perimetral de ejetados, estabilizar veículo invertido sob efeito gangorra e realizar a extração com técnica do abraço.',
  objetivoAula: 'Executar busca ativa perimetral de ejetados, estabilizar veículo invertido sob efeito gangorra e realizar a extração com técnica do abraço.',
  badge: '2 Vítimas (1 Vermelho, 1 Amarelo)',
  vitimas: [
    {
      id: 'capotamento_v1_ejetada',
      nomeCurto: 'V1 — Ejetada no Solo',
      papel: 'Passageira Arremessada',
      triagemReferencia: 'vermelho',
      local: 'solo',
      status: 'critica',
      parametrosXabcde: {
        xHemorragia: { valor: 'Laceração em couro cabeludo com sangramento moderado', critico: false },
        aViasAereas: { valor: 'Roncos e queda de língua; risco de aspiração', conduta: 'Tração mandibular (Jaw-Thrust), cânula orofaríngea e colar cervical' },
        bRespiracao: { fr: '10 irpm (Bradipneia / Cheyne-Stokes)', spo2: '85% (Hipóxia severa)', padrao: 'Padrão respiratório central instável' },
        cCirculacao: { fc: '58 bpm (Bradicardia reflexa)', pa: '165/100 mmHg (Hipertensão)', tec: '3 s', estado: 'Pulsos cheios centrais (Sinais de Hipertensão Intracraniana)' },
        dNeurologico: { glasgow: 'Glasgow 7 (Coma grave / Sem resposta verbal)', pupilas: 'Anisocoria (Midríase à direita, arreativa)', avdi: 'Inconsciente' },
        eExposicao: { temp: '35,0 °C (Hipotermia acentuada)', achados: 'Ejetada no solo a 3,4 m; escoriações múltiplas no dorso' },
      },
    },
    {
      id: 'capotamento_v2_interna',
      nomeCurto: 'V2 — Condutora no Veículo',
      papel: 'Condutora Invertida',
      triagemReferencia: 'amarelo',
      local: 'veiculo',
      status: 'estavel',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem hemorragia externa ativa', critico: false },
        aViasAereas: { valor: 'Via aérea pérvia, comunicando-se com a equipe', conduta: 'Estabilização cervical manual durante apoio' },
        bRespiracao: { fr: '22 irpm (Taquipneia leve por ansiedade)', spo2: '96%', padrao: 'Respiração simétrica, sem ruídos adventícios' },
        cCirculacao: { fc: '96 bpm', pa: '115/75 mmHg', tec: '2 s', estado: 'Pele corada, pulsos radiais simétricos' },
        dNeurologico: { glasgow: 'Glasgow 15 (Consciente e orientada)', pupilas: 'Isocóricas e fotorreagentes', avdi: 'Alerta' },
        eExposicao: { temp: '36,2 °C', achados: 'Suspensão pelo cinto; compressão na clavícula e pelve' },
      },
    },
  ],
  criarCena: () => copiarCenaTabletop(PRESET_CENARIO_3_CAPOTAMENTO),
  etapas: [
    {
      id: '360',
      numero: 1,
      nome: 'Volta 360°',
      subtitulo: 'Busca Ativa de Vítimas Ejetadas',
      decisaoTatica: 'Ao deparar-se com um capotamento com teto colapsado, qual a prioridade imediata do reconhecimento perimetral?',
      diretrizOperacional: 'A guarnição deve realizar varredura 360° em raio ampliado no solo e vegetação adjacente para localizar vítimas projetadas para fora do veículo antes de concentrar recursos exclusivamente no interior.',
      perguntaTurma: 'Ao deparar-se com um capotamento com teto colapsado, qual a prioridade imediata do reconhecimento perimetral?',
      orientacaoInstrutor: 'A guarnição deve realizar varredura 360° em raio ampliado no solo e vegetação adjacente para localizar vítimas projetadas para fora do veículo antes de concentrar recursos exclusivamente no interior.',
      focoCamera: 'panoramica',
      poseCamera: [2, 6, 7],
      cartoes: [
        {
          id: 'c3_360_ejetada',
          titulo: 'Vítima Ejetada Encontrada a 3,4 metros',
          categoria: 'cena',
          destaque: 'Varredura Perimetral Obrigatória',
          conteudo: 'A passageira foi projetada pelo para-brisa ou janela durante as rotações do carro, caindo em decúbito dorsal sobre o asfalto. Está inconsciente com sangramento cefálico.',
        },
      ],
    },
    {
      id: 'riscos',
      numero: 2,
      nome: 'Riscos da Cena',
      subtitulo: 'Combustível Invertido & Efeito Gangorra',
      decisaoTatica: 'Quais perigos estruturais e inflamáveis são específicos de um automóvel invertido sobre o teto?',
      diretrizOperacional: 'A guarnição deve neutralizar o vazamento de combustível por gravidade com linha de proteção carregada e anular o efeito gangorra instalando calços sob as colunas A e C para fixar o veículo ao solo.',
      perguntaTurma: 'Quais perigos estruturais e inflamáveis são específicos de um automóvel invertido sobre o teto?',
      orientacaoInstrutor: 'A guarnição deve neutralizar o vazamento de combustível por gravidade com linha de proteção carregada e anular o efeito gangorra instalando calços sob as colunas A e C para fixar o veículo ao solo.',
      focoCamera: 'riscos',
      poseCamera: [-1.5, 2.2, 3],
      cartoes: [
        {
          id: 'c3_risco_gasolina',
          titulo: 'Vazamento Contínuo de Gasolina',
          categoria: 'risco',
          destaque: 'Risco de Ignição Imediata',
          conteudo: 'Gasolina escorrendo diretamente sobre o teto e próximo aos cabos elétricos. Proteção imediata com linha de mangueira carregada e espuma/água neblinada.',
        },
        {
          id: 'c3_risco_gangorra',
          titulo: 'Instabilidade Crítica (Efeito Gangorra)',
          categoria: 'seguranca',
          destaque: 'Estabilização de 4 Pontos no Teto',
          conteudo: 'O peso do motor na frente faz o carro bascular para trás e para frente ao menor toque. Calços escalonados e blocos sob as colunas A e C são essenciais.',
        },
      ],
    },
    {
      id: 'cinematica',
      numero: 3,
      nome: 'Cinemática do Trauma',
      subtitulo: 'Biomecânica da Ejeção vs Retenção por Cinto',
      decisaoTatica: 'Como a biomecânica da ejeção agrava o prognóstico da vítima em relação à ocupante contida pelo cinto?',
      diretrizOperacional: 'A vítima ejetada sofre desacelerações secundárias e terciárias violentas contra o pavimento, resultando em traumatismo cranioencefálico grave e risco elevado de fratura da coluna. A condutora retida pelo cinto preserva a integridade estrutural contra impactos diretos.',
      perguntaTurma: 'Como a biomecânica da ejeção agrava o prognóstico da vítima em relação à ocupante contida pelo cinto?',
      orientacaoInstrutor: 'A vítima ejetada sofre desacelerações secundárias e terciárias violentas contra o pavimento, resultando em traumatismo cranioencefálico grave e risco elevado de fratura da coluna. A condutora retida pelo cinto preserva a integridade estrutural contra impactos diretos.',
      focoCamera: 'cinematica',
      poseCamera: [1.2, 2.2, 2.8],
      cartoes: [
        {
          id: 'c3_cin_ejetada',
          titulo: 'Trauma de Alta Energia por Ejeção',
          categoria: 'cinematica',
          destaque: 'TCE Grave + Lesão de Coluna Cervical',
          conteudo: 'A vítima ejetada colidiu o crânio contra a guia da calçada e o asfalto, sofrendo desaceleração brusca de massa encefálica com hemorragia intracraniana aguda.',
        },
      ],
    },
    {
      id: 'abordagem',
      numero: 4,
      nome: 'Abordagem da Vítima',
      subtitulo: 'Abordagem Simultânea em Dois Polos',
      decisaoTatica: 'Como dimensionar a divisão dos socorristas entre a vítima ejetada no asfalto e a condutora suspensa no carro?',
      diretrizOperacional: 'Destinar socorrista imediato para estabilização manual de coluna e abertura de vias aéreas de V1 no solo. Simultaneamente, socorrista 2 estabelece contato verbal com V2 para mantê-la imóvel enquanto se conclui o calçamento do veículo.',
      perguntaTurma: 'Como dimensionar a divisão dos socorristas entre a vítima ejetada no asfalto e a condutora suspensa no carro?',
      orientacaoInstrutor: 'Destinar socorrista imediato para estabilização manual de coluna e abertura de vias aéreas de V1 no solo. Simultaneamente, socorrista 2 estabelece contato verbal com V2 para mantê-la imóvel enquanto se conclui o calçamento do veículo.',
      focoCamera: 'vitima',
      poseCamera: [2.5, 1.4, 1.8],
      cartoes: [
        {
          id: 'c3_abo_divisao',
          titulo: 'Distribuição Tática de Socorristas',
          categoria: 'conduta',
          destaque: 'Prioridade Inicial no Solo',
          conteudo: 'V1 no solo está sem assistência imediata e pode broncoaspirar vômito. V2 no carro está lúcida e pode ser verbalmente instruída a aguardar o calçamento.',
        },
      ],
    },
    {
      id: 'triagem_xabcde',
      numero: 5,
      nome: 'Protocolo XABCDE',
      subtitulo: 'Avaliação XABCDE & Manejo do TCE Grave',
      decisaoTatica: 'Quais parâmetros clínicos no protocolo XABCDE determinam a gravidade da vítima ejetada?',
      diretrizOperacional: 'Identificar TCE grave no XABCDE em V1 (Glasgow 7, midríase unilateral, padrão de Cheyne-Stokes), exigindo manejo avançado de via aérea (intubação orotraqueal) e transporte urgente. V2 apresenta estabilidade clínica (Amarelo).',
      perguntaTurma: 'Quais parâmetros clínicos no protocolo XABCDE determinam a gravidade da vítima ejetada?',
      orientacaoInstrutor: 'Identificar TCE grave no XABCDE em V1 (Glasgow 7, midríase unilateral, padrão de Cheyne-Stokes), exigindo manejo avançado de via aérea (intubação orotraqueal) e transporte urgente. V2 apresenta estabilidade clínica (Amarelo).',
      focoCamera: 'vitima',
      poseCamera: [3, 1.2, 1.2],
      cartoes: [
        {
          id: 'c3_tri_ejetada_clinico',
          titulo: 'V1 (Solo): TCE Grave com Rebaixamento',
          categoria: 'clinico',
          destaque: 'Glasgow 7 — Prioridade Crítica (Vermelho)',
          conteudo: 'Inconsciente, pupilas anisocóricas (midríase direita), padrão respiratório irregular (Cheyne-Stokes), roncos na via aérea por queda de língua. Prioridade total.',
          condutaRecomendada: 'Abertura manual da via aérea por tração de mandíbula (Jaw-Thrust) + cânula orofaríngea + oxigênio a 100%.',
        },
      ],
    },
    {
      id: 'extracao',
      numero: 6,
      nome: 'Decisão de Extração',
      subtitulo: 'Técnica do Abraço no Veículo Invertido',
      decisaoTatica: 'Qual a técnica doutrinária obrigatória para desencarcerar a condutora suspensa pelo cinto de segurança no veículo capotado?',
      diretrizOperacional: 'Aplicar a técnica do abraço: o socorrista interno entra no veículo, posiciona-se sustentando firmemente o tronco e o dorso da vítima contra seu corpo ou prancha curta, e apenas então libera o cinto de segurança, descendo-a suavemente.',
      perguntaTurma: 'Qual a técnica doutrinária obrigatória para desencarcerar a condutora suspensa pelo cinto de segurança no veículo capotado?',
      orientacaoInstrutor: 'Aplicar a técnica do abraço: o socorrista interno entra no veículo, posiciona-se sustentando firmemente o tronco e o dorso da vítima contra seu corpo ou prancha curta, e apenas então libera o cinto de segurança, descendo-a suavemente.',
      focoCamera: 'extracao',
      poseCamera: [0, 2, 2.5],
      cartoes: [
        {
          id: 'c3_ext_abraco',
          titulo: 'Manobra do Abraço de Sustentação',
          categoria: 'extracao',
          destaque: 'Evitar Lesão Cervical Secundária por Queda',
          conteudo: 'O socorrista apoia o peso da condutora contra o próprio tórax ou banco com auxílio de prancha curta. Solta-se o cinto e desce-se a vítima suavemente ao solo.',
        },
      ],
    },
  ],
}

/**
 * Cenário 4: Carro Sobre Outro Veículo — Estabilização Pesada
 */
const CENARIO_4_CARRO_SOBRE_OUTRO: CenarioDidatico = {
  id: 'cenario-4',
  idPreset: 'cenario-4',
  titulo: 'Cenário 4: Carro Sobre Outro Veículo',
  subtitulo: 'Estabilização com V-Strut no Solo e Fita Catraca em Monobloco',
  descricao: 'Veículo superior encavalado e tombado sobre a traseira/teto de sedã inferior. V1 retido sob a carga com dor torácica; V2 no veículo superior elevado.',
  objetivoOperacional: 'Aplicar a doutrina de salvamento veicular técnico: ancorar V-Struts com a sapata no solo firme e a cabeça no carro a ser escorado, e aplicar a fita catraca unindo os dois veículos formando um bloco preso (monobloco) antes de qualquer intervenção de desencarceramento.',
  objetivoAula: 'Aplicar a doutrina de salvamento veicular técnico: ancorar V-Struts com a sapata no solo firme e a cabeça no carro a ser escorado, e aplicar a fita catraca unindo os dois veículos formando um bloco preso (monobloco) antes de qualquer intervenção de desencarceramento.',
  badge: '2 Vítimas (Estabilização Pesada)',
  vitimas: [
    {
      id: 'sobre_outro_v1_inferior',
      nomeCurto: 'V1 — Condutor Inferior',
      papel: 'Condutor Sob a Carga',
      triagemReferencia: 'vermelho',
      local: 'veiculo',
      status: 'critica',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem sangramento externo ativo evidente', critico: false },
        aViasAereas: { valor: 'Pérvia; relata opressão torácica e falta de ar', conduta: 'Estabilização cervical preventiva' },
        bRespiracao: { fr: '26 irpm (Dispneia restritiva)', spo2: '90% (Hipóxia)', padrao: 'Expansão torácica impedida pelo teto colapsado' },
        cCirculacao: { fc: '116 bpm (Taquicardia compensatória)', pa: '100/65 mmHg', tec: '3 s', estado: 'Pele pálida e sudoreica; pulsos finos' },
        dNeurologico: { glasgow: 'Glasgow 14 (Lúcido, queixa de dor severa)', pupilas: 'Isocóricas e fotorreagentes', avdi: 'Alerta' },
        eExposicao: { temp: '35,8 °C', achados: 'Confinamento extremo sob o teto colapsado; risco de asfixia mecânica' },
      },
    },
    {
      id: 'sobre_outro_v2_superior',
      nomeCurto: 'V2 — Ocupante Superior',
      papel: 'Condutor Elevado',
      triagemReferencia: 'amarelo',
      local: 'veiculo',
      status: 'estavel',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem hemorragias', critico: false },
        aViasAereas: { valor: 'Via aérea livre, sem queixas respiratórias', conduta: 'Orientação postural para não movimentar o conjunto' },
        bRespiracao: { fr: '18 irpm (Eupneico)', spo2: '98%', padrao: 'Respiração normal e simétrica' },
        cCirculacao: { fc: '88 bpm', pa: '120/80 mmHg', tec: '< 2 s', estado: 'Pele corada, normotenso' },
        dNeurologico: { glasgow: 'Glasgow 15 (Orientado, assustado com a inclinação)', pupilas: 'Isocóricas', avdi: 'Alerta' },
        eExposicao: { temp: '36,6 °C', achados: 'Sem dor ou lesões mecânicas aparentes; veículo elevado' },
      },
    },
  ],
  criarCena: () => copiarCenaTabletop(PRESET_CENARIO_4_CARRO_SOBRE_OUTRO),
  etapas: [
    {
      id: '360',
      numero: 1,
      nome: 'Volta 360°',
      subtitulo: 'Reconhecimento de Carga Suspensa & Centro de Massa',
      decisaoTatica: 'Qual avaliação estrutural prioritária deve ser feita ao identificar uma ocorrência de carro sobre outro?',
      diretrizOperacional: 'Reconhecer imediatamente a condição de carga suspensa em equilíbrio instável. Mapear pontos de contato, avaliar eixos de deslizamento e estabelecer zona quente de exclusão em raio mínimo de 5 metros até a consolidação do escoramento.',
      perguntaTurma: 'Qual avaliação estrutural prioritária deve ser feita ao identificar uma ocorrência de carro sobre outro?',
      orientacaoInstrutor: 'Reconhecer imediatamente a condição de carga suspensa em equilíbrio instável. Mapear pontos de contato, avaliar eixos de deslizamento e estabelecer zona quente de exclusão em raio mínimo de 5 metros até a consolidação do escoramento.',
      focoCamera: 'panoramica',
      poseCamera: [0, 8, 10],
      cartoes: [
        {
          id: 'c4_360_pontos_contato',
          titulo: 'Mapeamento dos Pontos de Contato',
          categoria: 'cena',
          destaque: 'Identificar Linhas de Escorregamento',
          conteudo: 'O carro superior apoia o assoalho e a lateral contra o teto e o porta-malas do sedã inferior. Não há engate mecânico soldado: é puramente atrito e peso.',
          detalhes: [
            'Avaliar inclinação lateral (risco de tombamento)',
            'Avaliar inclinação longitudinal (risco de deslizamento)',
            'Delimitar zona quente estrita em raio de 5 metros',
          ],
        },
      ],
    },
    {
      id: 'riscos',
      numero: 2,
      nome: 'Riscos da Cena',
      subtitulo: 'Doutrina de Estabilização: V-Strut no Solo & Fita Catraca em Bloco',
      decisaoTatica: 'Como deve ser montado o sistema de estabilização pesada com V-Strut e Fita Catraca segundo a doutrina técnica?',
      diretrizOperacional: 'A sapata do V-Strut deve estar firmemente apoiada no solo e a cabeça serrilhada travada na estrutura resistente do carro superior. A fita catraca deve abraçar e conectar ambos os veículos, tracionando-os até formar um bloco único rígido (monobloco) que impeça deslocamentos mútuos.',
      perguntaTurma: 'Como deve ser montado o sistema de estabilização pesada com V-Strut e Fita Catraca segundo a doutrina técnica?',
      orientacaoInstrutor: 'A sapata do V-Strut deve estar firmemente apoiada no solo e a cabeça serrilhada travada na estrutura resistente do carro superior. A fita catraca deve abraçar e conectar ambos os veículos, tracionando-os até formar um bloco único rígido (monobloco) que impeça deslocamentos mútuos.',
      focoCamera: 'riscos',
      poseCamera: [-2, 2.5, 3.5],
      cartoes: [
        {
          id: 'c4_risco_vstrut_solo',
          titulo: 'Posicionamento do V-Strut: Sapata no Chão & Cabeça no Carro',
          categoria: 'seguranca',
          destaque: 'Vetor de Apoio Firme contra a Gravidade',
          conteudo: 'A sapata articulada do V-Strut deve ser assentada diretamente no solo plano e estável. A haste telescópica estende-se em ângulo de 45° a 60°, encaixando a cabeça serrilhada na coluna ou longarina do carro superior que precisa ser escorado.',
          condutaRecomendada: 'Travar os pinos rápidos da haste e tensionar a cinta integrada da base.',
        },
        {
          id: 'c4_risco_fita_catraca_bloco',
          titulo: 'Fita Catraca: União em Bloco Único Preso (Monobloco)',
          categoria: 'seguranca',
          destaque: 'Solidarização dos Dois Veículos',
          conteudo: 'A fita catraca é passada abraçando o chassi e colunas dos dois carros simultaneamente. Ao catracar e tensionar, os dois veículos deixam de ser corpos soltos e passam a se comportar como um único monobloco rígido preso, impedindo que o carro superior deslize.',
          condutaRecomendada: 'Catracar até eliminar qualquer folga mecânica entre os veículos.',
        },
        {
          id: 'c4_risco_regra_ouro',
          titulo: 'Regra de Ouro do Salvamento Pesado',
          categoria: 'risco',
          destaque: 'Proibição Absoluta de Adentrar sem Escoramento',
          conteudo: 'NENHUM socorrista pode colocar a cabeça ou o corpo sob a projeção do carro superior antes que os V-Struts e as fitas catraca estejam completamente travados.',
        },
      ],
    },
    {
      id: 'cinematica',
      numero: 3,
      nome: 'Cinemática do Trauma',
      subtitulo: 'Compressão Estática Contínua no Habitáculo',
      decisaoTatica: 'Qual o mecanismo de trauma contínuo sofrido pelo condutor do veículo inferior prensado pela carga?',
      diretrizOperacional: 'O rebaixamento do teto sobre o tórax do ocupante impõe asfixia mecânica posicional contínua e risco de síndrome de esmagamento (crush syndrome), demandando monitoramento do tempo de compressão e liberação descompressiva controlada.',
      perguntaTurma: 'Qual o mecanismo de trauma contínuo sofrido pelo condutor do veículo inferior prensado pela carga?',
      orientacaoInstrutor: 'O rebaixamento do teto sobre o tórax do ocupante impõe asfixia mecânica posicional contínua e risco de síndrome de esmagamento (crush syndrome), demandando monitoramento do tempo de compressão e liberação descompressiva controlada.',
      focoCamera: 'cinematica',
      poseCamera: [0.8, 2.2, 2.5],
      cartoes: [
        {
          id: 'c4_cin_asfixia',
          titulo: 'Asfixia Traumática e Restrição Respiratória',
          categoria: 'cinematica',
          destaque: 'Compressão do Gradil Costal Superior',
          conteudo: 'O teto do sedã rebaixou 32 cm, comprimindo o espaço livre acima do condutor. A expansão torácica fica limitada e a musculatura respiratória entra em fadiga.',
        },
      ],
    },
    {
      id: 'abordagem',
      numero: 4,
      nome: 'Abordagem da Vítima',
      subtitulo: 'Comunicação Verbal à Distância & Acesso Seguro',
      decisaoTatica: 'Qual a conduta da guarnição em relação aos ocupantes enquanto a estabilização pesada é armada?',
      diretrizOperacional: 'Manter comunicação verbal diretiva à distância orientando o ocupante superior a permanecer estático sem oscilar o veículo e acalmando o condutor inferior, com proibição estrita de entrada de socorristas antes da conclusão do monobloco.',
      perguntaTurma: 'Qual a conduta da guarnição em relação aos ocupantes enquanto a estabilização pesada é armada?',
      orientacaoInstrutor: 'Manter comunicação verbal diretiva à distância orientando o ocupante superior a permanecer estático sem oscilar o veículo e acalmando o condutor inferior, com proibição estrita de entrada de socorristas antes da conclusão do monobloco.',
      focoCamera: 'vitima',
      poseCamera: [-1.2, 1.6, 1.2],
      cartoes: [
        {
          id: 'c4_abo_verbal',
          titulo: 'Gerenciamento Verbal de Pânico',
          categoria: 'conduta',
          destaque: 'Evitar Movimentos Bruscos',
          conteudo: '"Estamos estabilizando o veículo com escoras de resgate. Não tente se mexer ou balançar o carro. Em instantes entraremos para cuidar de você."',
        },
      ],
    },
    {
      id: 'triagem_xabcde',
      numero: 5,
      nome: 'Protocolo XABCDE',
      subtitulo: 'Avaliação Clínica com Carga Neutralizada',
      decisaoTatica: 'Com os veículos estabilizados em monobloco, qual a categorização clínica e o plano de prioridade das vítimas no XABCDE?',
      diretrizOperacional: 'O condutor inferior (V1) apresenta dispneia restritiva, taquipneia (26 irpm) e hipoxemia = Prioridade Crítica (Vermelho). O ocupante superior (V2) está consciente e sem lesões de risco iminente = Estável (Amarelo). Prioridade de extração para V1.',
      perguntaTurma: 'Com os veículos estabilizados em monobloco, qual a categorização clínica e o plano de prioridade das vítimas no XABCDE?',
      orientacaoInstrutor: 'O condutor inferior (V1) apresenta dispneia restritiva, taquipneia (26 irpm) e hipoxemia = Prioridade Crítica (Vermelho). O ocupante superior (V2) está consciente e sem lesões de risco iminente = Estável (Amarelo). Prioridade de extração para V1.',
      focoCamera: 'vitima',
      poseCamera: [-0.6, 1.4, 0.8],
      cartoes: [
        {
          id: 'c4_tri_inferior_vermelho',
          titulo: 'V1 Inferior: Prioridade Crítica (Comprometimento B/C no XABCDE)',
          categoria: 'clinico',
          destaque: 'Dispneia Restritiva + Hipóxia',
          conteudo: 'Queixa de peso esmagador no tórax, SpO2 90%, FC 116 bpm, PA 100/65 mmHg. Risco de choque por descompressão repentina ou colapso respiratório.',
        },
        {
          id: 'c4_tri_superior_amarelo',
          titulo: 'V2 Superior: Estável Confinado (Amarelo)',
          categoria: 'clinico',
          destaque: 'Sem Lesões Graves Imediatas',
          conteudo: 'Glasgow 15, sem sangramentos, apenas vertigem e ansiedade pela posição elevada. Retirada aguarda rota segura sem comprometer o carro inferior.',
        },
      ],
    },
    {
      id: 'extracao',
      numero: 6,
      nome: 'Decisão de Extração',
      subtitulo: 'Extração Lateral do Inferior sem Tocar no Bloco',
      decisaoTatica: 'É permitido cortar o teto ou as colunas do veículo de baixo para retirar o condutor retido?',
      diretrizOperacional: 'Proibido cortar as colunas ou o teto do veículo inferior, pois sustentam a carga do conjunto. A extração deve ser estritamente lateral, através da remoção cirúrgica das portas esquerdas e retirada do condutor alinhado em prancha longa.',
      perguntaTurma: 'É permitido cortar o teto ou as colunas do veículo de baixo para retirar o condutor retido?',
      orientacaoInstrutor: 'Proibido cortar as colunas ou o teto do veículo inferior, pois sustentam a carga do conjunto. A extração deve ser estritamente lateral, através da remoção cirúrgica das portas esquerdas e retirada do condutor alinhado em prancha longa.',
      focoCamera: 'extracao',
      poseCamera: [-2.5, 2.2, 1.8],
      cartoes: [
        {
          id: 'c4_ext_rota_lateral',
          titulo: 'Abertura Lateral sem Alívio Estrutural de Teto',
          categoria: 'extracao',
          destaque: 'Preservar Colunas Sustentadoras',
          conteudo: 'Remoção cirúrgica da porta dianteira esquerda com tesoura e expansor. O condutor é retirado em prancha longa deslizada pela lateral sem alterar o escoramento.',
        },
      ],
    },
  ],
}

/**
 * Cenário 5: Colisão com Duas Vítimas Estáveis (Retirada Alinhada a 0°)
 */
const CENARIO_5_DUAS_ESTAVEIS: CenarioDidatico = {
  id: 'cenario-5',
  idPreset: 'cenario-5',
  titulo: 'Cenário 5: Colisão com Duas Vítimas Estáveis',
  subtitulo: 'Manejo de Espaço e Retirada Alinhada a 0° (Vítimas Estáveis)',
  descricao: 'Sedã colidido contra defensa de proteção com portas deformadas e emperradas. V1 e V2 lúcidos com dor cervical e lombar.',
  objetivoOperacional: 'Executar desencarceramento técnico controlado sem pressa clínica: abertura cirúrgica de portas emperradas, alívio de espaço e retirada alinhada a 0° com prancha longa.',
  objetivoAula: 'Executar desencarceramento técnico controlado sem pressa clínica: abertura cirúrgica de portas emperradas, alívio de espaço e retirada alinhada a 0° com prancha longa.',
  badge: '2 Vítimas (2 Verdes)',
  vitimas: [
    {
      id: 'slide_53_v1_motorista',
      nomeCurto: 'V1 — Motorista',
      papel: 'Condutor',
      triagemReferencia: 'verde',
      local: 'veiculo',
      status: 'estavel',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem hemorragias ativas', critico: false },
        aViasAereas: { valor: 'Pérvia; queixa de cervicalgia moderada', conduta: 'Aplicação técnica de colar cervical (RMC)' },
        bRespiracao: { fr: '16 irpm (Eupneico)', spo2: '99%', padrao: 'Murmúrios vesiculares preservados' },
        cCirculacao: { fc: '72 bpm (Normocárdico)', pa: '120/75 mmHg (Normotenso)', tec: '< 2 s', estado: 'Pele corada, quente e seca' },
        dNeurologico: { glasgow: 'Glasgow 15 (Orientado no tempo e espaço)', pupilas: 'Isocóricas fotorreagentes', avdi: 'Alerta' },
        eExposicao: { temp: '36,6 °C', achados: 'Dor em região cervical e trapézio; sem deformidades ósseas aparentes' },
      },
    },
    {
      id: 'slide_53_v2_passageiro',
      nomeCurto: 'V2 — Passageiro',
      papel: 'Passageiro',
      triagemReferencia: 'verde',
      local: 'veiculo',
      status: 'estavel',
      parametrosXabcde: {
        xHemorragia: { valor: 'Sem hemorragias', critico: false },
        aViasAereas: { valor: 'Via aérea pérvia, sem esforço ventilatório', conduta: 'Restrição de Movimento da Coluna com colar cervical' },
        bRespiracao: { fr: '16 irpm (Eupneico)', spo2: '98%', padrao: 'Expansão torácica normal' },
        cCirculacao: { fc: '76 bpm', pa: '118/72 mmHg', tec: '< 2 s', estado: 'Hemodinâmica totalmente estável' },
        dNeurologico: { glasgow: 'Glasgow 15 (Orientado, comunicativo)', pupilas: 'Isocóricas', avdi: 'Alerta' },
        eExposicao: { temp: '36,5 °C', achados: 'Queixa leve de dor lombar e cervical; sem perda de sensibilidade ou motricidade' },
      },
    },
  ],
  criarCena: () => copiarCenaTabletop(PRESET_CENARIO_5_DUAS_ESTAVEIS),
  etapas: [
    {
      id: '360',
      numero: 1,
      nome: 'Volta 360°',
      subtitulo: 'Dimensionamento & Controle do Tráfego',
      decisaoTatica: 'Em colisão moderada contra defensa onde as vítimas estão conscientes, qual o risco tático na aproximação?',
      diretrizOperacional: 'A guarnição deve prevenir ativamente a autoextração descoordenada: emitir comando verbal imediato para que os ocupantes permaneçam sentados e imóveis, evitando o agravamento de potenciais fraturas instáveis de coluna.',
      perguntaTurma: 'Em colisão moderada contra defensa onde as vítimas estão conscientes, qual o risco tático na aproximação?',
      orientacaoInstrutor: 'A guarnição deve prevenir ativamente a autoextração descoordenada: emitir comando verbal imediato para que os ocupantes permaneçam sentados e imóveis, evitando o agravamento de potenciais fraturas instáveis de coluna.',
      focoCamera: 'panoramica',
      poseCamera: [0, 6, 8],
      cartoes: [
        {
          id: 'c5_360_autoextracao',
          titulo: 'Risco de Autoextração Desordenada',
          categoria: 'cena',
          destaque: 'Instrução Verbal Imediata',
          conteudo: 'A guarnição deve ordenar em voz alta: "Permaneçam sentados e não movimentem o pescoço. O resgate está em andamento."',
        },
      ],
    },
    {
      id: 'riscos',
      numero: 2,
      nome: 'Riscos da Cena',
      subtitulo: 'Calçamento Primário & Portas Emperradas',
      decisaoTatica: 'Por que o calçamento sob as quatro colunas é obrigatório mesmo com o veículo apoiado nas quatro rodas?',
      diretrizOperacional: 'O calçamento escalonado elimina o curso elástico da suspensão do veículo, impedindo que as oscilações e forças geradas pelas ferramentas hidráulicas sejam transferidas para a coluna vertebral dos pacientes.',
      perguntaTurma: 'Por que o calçamento sob as quatro colunas é obrigatório mesmo com o veículo apoiado nas quatro rodas?',
      orientacaoInstrutor: 'O calçamento escalonado elimina o curso elástico da suspensão do veículo, impedindo que as oscilações e forças geradas pelas ferramentas hidráulicas sejam transferidas para a coluna vertebral dos pacientes.',
      focoCamera: 'riscos',
      poseCamera: [-2.5, 2, 3],
      cartoes: [
        {
          id: 'c5_risco_suspensao',
          titulo: 'Anulação da Elasticidade da Suspensão',
          categoria: 'seguranca',
          destaque: 'Calçamento Escalonado nas 4 Colunas',
          conteudo: 'Inserção de calços escalonados sob as colunas A e C para transferir o peso do carro diretamente para o solo, eliminando o balanço elástico.',
        },
      ],
    },
    {
      id: 'cinematica',
      numero: 3,
      nome: 'Cinemática do Trauma',
      subtitulo: 'Efeito Chicote (Whiplash) & Suspeita de TRM',
      decisaoTatica: 'Qual o mecanismo de lesão decorrente da desaceleração brusca com retenção por cinto em via rápida?',
      diretrizOperacional: 'Aceleração e desaceleração brusca de cabeça e tronco (efeito chicote / whiplash), gerando alto risco de entorses ligamentares, fraturas de processos espinhosos cervicais e subluxações vertebrais que exigem restrição rígida de movimento da coluna (RMC).',
      perguntaTurma: 'Qual o mecanismo de lesão decorrente da desaceleração brusca com retenção por cinto em via rápida?',
      orientacaoInstrutor: 'Aceleração e desaceleração brusca de cabeça e tronco (efeito chicote / whiplash), gerando alto risco de entorses ligamentares, fraturas de processos espinhosos cervicais e subluxações vertebrais que exigem restrição rígida de movimento da coluna (RMC).',
      focoCamera: 'cinematica',
      poseCamera: [0, 2.2, 3.5],
      cartoes: [
        {
          id: 'c5_cin_chicote',
          titulo: 'Hiperextensão e Hiperflexão Cervical',
          categoria: 'cinematica',
          destaque: 'Mecanismo de Aceleração/Desaceleração',
          conteudo: 'Ambos os ocupantes relatam dor à palpação na linha média dos processos espinhosos de C4 a C7, indicando protocolo rigoroso de restrição de movimento da coluna (RMC).',
        },
      ],
    },
    {
      id: 'abordagem',
      numero: 4,
      nome: 'Abordagem da Vítima',
      subtitulo: 'Abertura Cirúrgica de Portas Emperradas',
      decisaoTatica: 'Como operar a abertura da porta emperrada sem transmitir torção e vibração ao habitáculo?',
      diretrizOperacional: 'Inserir as pontas do expansor hidráulico no vértice da fechadura e forçar o afastamento em plano paralelo à estrutura, rompendo o trinco sem apoiar braços da ferramenta sobre a coluna dos ocupantes.',
      perguntaTurma: 'Como operar a abertura da porta emperrada sem transmitir torção e vibração ao habitáculo?',
      orientacaoInstrutor: 'Inserir as pontas do expansor hidráulico no vértice da fechadura e forçar o afastamento em plano paralelo à estrutura, rompendo o trinco sem apoiar braços da ferramenta sobre a coluna dos ocupantes.',
      focoCamera: 'vitima',
      poseCamera: [-1.5, 1.6, 1.2],
      cartoes: [
        {
          id: 'c5_abo_porta',
          titulo: 'Abertura com Expansor Hidráulico',
          categoria: 'extracao',
          destaque: 'Cisalhamento Suave da Fechadura',
          conteudo: 'Aplicação das pontas do expansor próximo à maçaneta para estourar o pino-guia sem torcer a coluna B.',
        },
      ],
    },
    {
      id: 'triagem_xabcde',
      numero: 5,
      nome: 'Protocolo XABCDE',
      subtitulo: 'Confirmação da Estabilidade Clínica',
      decisaoTatica: 'Qual a avaliação pelo protocolo XABCDE e a diretriz de atendimento para duas vítimas estáveis com queixa cervical?',
      diretrizOperacional: 'Ambas as vítimas apresentam estabilidade clínica no XABCDE (Glasgow 15, vias aéreas pérvias, hemodinâmica normal). O tempo não é fator crítico; a prioridade absoluta deve ser a execução técnica impecável da Restrição de Movimento da Coluna (RMC).',
      perguntaTurma: 'Qual a avaliação pelo protocolo XABCDE e a diretriz de atendimento para duas vítimas estáveis com queixa cervical?',
      orientacaoInstrutor: 'Ambas as vítimas apresentam estabilidade clínica no XABCDE (Glasgow 15, vias aéreas pérvias, hemodinâmica normal). O tempo não é fator crítico; a prioridade absoluta deve ser a execução técnica impecável da Restrição de Movimento da Coluna (RMC).',
      focoCamera: 'vitima',
      poseCamera: [-0.8, 1.4, 0.8],
      cartoes: [
        {
          id: 'c5_tri_verde',
          titulo: 'XABCDE Estável: Prioridade para Técnica de RMC',
          categoria: 'clinico',
          destaque: 'Estabilidade Hemodinâmica e Neurológica',
          conteudo: 'V1 e V2 com sinais vitais dentro dos parâmetros normais. Colar cervical aplicado, imobilização com KED ou prancha longa alinhada.',
        },
      ],
    },
    {
      id: 'extracao',
      numero: 6,
      nome: 'Decisão de Extração',
      subtitulo: 'Retirada Alinhada a 0° (Rota Longitudinal)',
      decisaoTatica: 'Por que a extração a 0° (rota longitudinal) é a manobra de escolha para pacientes com suspeita de lesão na coluna cervical?',
      diretrizOperacional: 'A rota longitudinal a 0° permite deslizar a vítima em linha reta perfeitamente alinhada sobre a prancha longa pelo compartimento traseiro ou teto rebatido, com zero rotação ou flexão lateral do eixo crânio-caudal.',
      perguntaTurma: 'Por que a extração a 0° (rota longitudinal) é a manobra de escolha para pacientes com suspeita de lesão na coluna cervical?',
      orientacaoInstrutor: 'A rota longitudinal a 0° permite deslizar a vítima em linha reta perfeitamente alinhada sobre a prancha longa pelo compartimento traseiro ou teto rebatido, com zero rotação ou flexão lateral do eixo crânio-caudal.',
      focoCamera: 'extracao',
      poseCamera: [0, 2.5, -4],
      cartoes: [
        {
          id: 'c5_ext_zero_grau',
          titulo: 'Extração a 0° com Prancha Longa por Trás',
          categoria: 'extracao',
          destaque: 'Preservação Absoluta do Eixo Crânio-Caudal',
          conteudo: 'Rebatimento dos encostos traseiros, inserção da prancha longa por trás dos bancos e deslizamento suave em bloco com quatro socorristas coordenados.',
        },
      ],
    },
  ],
}

/**
 * Cenário 6: Atropelamento e Choque em Poste
 */
const CENARIO_6_ATROPELAMENTO: CenarioDidatico = {
  id: 'cenario-6',
  idPreset: 'cenario-6',
  titulo: 'Cenário 6: Atropelamento e Choque em Poste',
  subtitulo: 'Controle de Hemorragia Exsanguinante e Risco Elétrico',
  descricao: 'Veículo que atropelou pedestre e colidiu contra poste de iluminação. Cabo energizado caído na via a 3 m da vítima no solo com sangramento maciço em coxa.',
  objetivoOperacional: 'Controlar hemorragia exangue no XABCDE (Letra X precoce com torniquete tático), isolar risco de eletrocussão por tensão de passo e imobilizar em maca tipo concha.',
  objetivoAula: 'Controlar hemorragia exangue no XABCDE (Letra X precoce com torniquete tático), isolar risco de eletrocussão por tensão de passo e imobilizar em maca tipo concha.',
  badge: '1 Vítima (Crítica / Hemorragia)',
  vitimas: [
    {
      id: 'atropelamento-vitima',
      nomeCurto: 'Pedestre no Solo',
      papel: 'Pedestre Atropelado',
      triagemReferencia: 'vermelho',
      local: 'solo',
      status: 'critica',
      parametrosXabcde: {
        xHemorragia: { valor: 'Amputação parcial abaixo do joelho esquerdo; sangramento arterial maciço pulsátil', critico: true },
        aViasAereas: { valor: 'Via aérea pérvia; gemência álgica intensa', conduta: 'Abertura manual e colar cervical imediato' },
        bRespiracao: { fr: '24 irpm (Taquipneia)', spo2: '92%', padrao: 'Respiração rápida e suspirosa' },
        cCirculacao: { fc: '130 bpm (Taquicardia severa)', pa: '90/60 mmHg (Choque Classe III)', tec: '3 s', estado: 'Pele fria, sudoreica e pálida; torniquete tático aplicado com sucesso' },
        dNeurologico: { glasgow: 'Glasgow 12 (Sonolento, resposta confusa à dor)', pupilas: 'Isocóricas fotorreagentes', avdi: 'Reage a estímulo Verbal/Dor' },
        eExposicao: { temp: '35,2 °C (Hipotermia)', achados: 'Amputação em MID e suspeita de fratura pélvica; isolar do cabo e aquecer' },
      },
    },
  ],
  criarCena: () => copiarCenaTabletop(PRESET_CENARIO_6_ATROPELAMENTO),
  etapas: [
    {
      id: '360',
      numero: 1,
      nome: 'Volta 360°',
      subtitulo: 'Identificação Precoce do Risco Elétrico',
      decisaoTatica: 'Ao aproximar-se de um veículo colidido contra poste elétrico, qual a primeira verificação de segurança da cena?',
      diretrizOperacional: 'Inspecionar visualmente cabos condutores partidos na estrutura do veículo ou no solo. Tratar todo cabo como energizado e delimitar raio de exclusão de no mínimo 10 metros para prevenir descargas elétricas na equipe.',
      perguntaTurma: 'Ao aproximar-se de um veículo colidido contra poste elétrico, qual a primeira verificação de segurança da cena?',
      orientacaoInstrutor: 'Inspecionar visualmente cabos condutores partidos na estrutura do veículo ou no solo. Tratar todo cabo como energizado e delimitar raio de exclusão de no mínimo 10 metros para prevenir descargas elétricas na equipe.',
      focoCamera: 'panoramica',
      poseCamera: [0, 7, 9],
      cartoes: [
        {
          id: 'c6_360_cabo',
          titulo: 'Cabo Energizado de Alta Tensão Caído',
          categoria: 'risco',
          destaque: 'Risco Mortal de Eletrocussão da Equipe',
          conteudo: 'Fio elétrico rompido do transformador caído sobre a pista a 3 metros da vítima. O solo ao redor pode estar energizado em círculos concêntricos.',
        },
      ],
    },
    {
      id: 'riscos',
      numero: 2,
      nome: 'Riscos da Cena',
      subtitulo: 'Tensão de Passo & Perímetro de Segurança',
      decisaoTatica: 'O que caracteriza a tensão de passo e qual o protocolo de movimentação caso seja necessário evacuar da zona energizada?',
      diretrizOperacional: 'A corrente dissipa-se no solo em gradiente de voltagem circular. Para evitar choque elétrico entre os membros inferiores, deve-se manter os pés unidos e arrastá-los sem tirar o contato do solo (passo de arrasto), solicitando desligamento prioritário à concessionária.',
      perguntaTurma: 'O que caracteriza a tensão de passo e qual o protocolo de movimentação caso seja necessário evacuar da zona energizada?',
      orientacaoInstrutor: 'A corrente dissipa-se no solo em gradiente de voltagem circular. Para evitar choque elétrico entre os membros inferiores, deve-se manter os pés unidos e arrastá-los sem tirar o contato do solo (passo de arrasto), solicitando desligamento prioritário à concessionária.',
      focoCamera: 'riscos',
      poseCamera: [1.5, 2.5, 3],
      cartoes: [
        {
          id: 'c6_risco_passo',
          titulo: 'Protocolo de Tensão de Passo',
          categoria: 'seguranca',
          destaque: 'Passos Curtos Arrastados ou Isolamento de 10m',
          conteudo: 'A concessionária de energia deve ser acionada imediatamente para desligamento do circuito. Não tocar no carro nem no poste até confirmação de desenergização.',
          condutaRecomendada: 'Isolar com cones a 10 metros e aguardar corte da energia ou aproximação estritamente segura.',
        },
      ],
    },
    {
      id: 'cinematica',
      numero: 3,
      nome: 'Cinemática do Trauma',
      subtitulo: 'Tríade de Waddell em Atropelamento',
      decisaoTatica: 'Quais as três fases de impacto na cinemática do atropelamento de pedestre (Tríade de Waddell)?',
      diretrizOperacional: 'Reconhecer as três fases: 1) Impacto do para-choque contra pernas/fêmur; 2) Projeção do tronco e crânio sobre capô e para-brisa; 3) Queda e desaceleração secundária contra o pavimento.',
      perguntaTurma: 'Quais as três fases de impacto na cinemática do atropelamento de pedestre (Tríade de Waddell)?',
      orientacaoInstrutor: 'Reconhecer as três fases: 1) Impacto do para-choque contra pernas/fêmur; 2) Projeção do tronco e crânio sobre capô e para-brisa; 3) Queda e desaceleração secundária contra o pavimento.',
      focoCamera: 'cinematica',
      poseCamera: [-0.8, 2, 2.5],
      cartoes: [
        {
          id: 'c6_cin_waddell',
          titulo: 'Tríade de Impacto de Waddell',
          categoria: 'cinematica',
          destaque: 'Impacto Triplo em Sequência',
          conteudo: 'Para-brisa com quebra em padrão de teia de aranha confirma impacto cefálico de alta energia antes da queda violenta no solo.',
        },
      ],
    },
    {
      id: 'abordagem',
      numero: 4,
      nome: 'Abordagem da Vítima',
      subtitulo: 'Prioridade Absoluta: Letra X (Hemorragia Exsanguinante)',
      decisaoTatica: 'Identificada hemorragia arterial exsanguinante em coxa da vítima no solo, qual ação precede a via aérea?',
      diretrizOperacional: 'Doutrina do XABCDE (PHTLS): a letra X (hemorragia exsanguinante) tem prioridade absoluta sobre A. Aplicar torniquete tático de 5 a 7 cm proximal à lesão (ou na raiz do membro), girando a haste até a cessação do pulso e do sangramento arterial, e registrar o horário.',
      perguntaTurma: 'Identificada hemorragia arterial exsanguinante em coxa da vítima no solo, qual ação precede a via aérea?',
      orientacaoInstrutor: 'Doutrina do XABCDE (PHTLS): a letra X (hemorragia exsanguinante) tem prioridade absoluta sobre A. Aplicar torniquete tático de 5 a 7 cm proximal à lesão (ou na raiz do membro), girando a haste até a cessação do pulso e do sangramento arterial, e registrar o horário.',
      focoCamera: 'vitima',
      poseCamera: [-1.2, 1.4, 1.2],
      cartoes: [
        {
          id: 'c6_abo_torniquete',
          titulo: 'Aplicação Imediata de Torniquete Tático',
          categoria: 'conduta',
          destaque: 'Letra X em Menos de 60 Segundos',
          conteudo: 'Aplicação de torniquete tático (CAT / SOFTT) 5 a 7 cm acima do sítio da lesão (ou raiz da coxa se fratura exposta complexa). Girar a haste até cessar totalmente o sangramento arterial.',
          condutaRecomendada: 'Anotar o horário exato da aplicação do torniquete na fita frontal da haste.',
        },
      ],
    },
    {
      id: 'triagem_xabcde',
      numero: 5,
      nome: 'Protocolo XABCDE',
      subtitulo: 'Reavaliação Pós-Torniquete & Parâmetros XABCDE',
      decisaoTatica: 'Controlada a hemorragia com torniquete, quais são os parâmetros do XABCDE a serem reavaliados e qual a prioridade clínica?',
      diretrizOperacional: 'Vítima em choque hipovolêmico classe III no XABCDE (C: taquicardia 130 bpm, PA 90/60 mmHg, TEC 3 s). Prioridade Crítica (Vermelho). Instalar oxigenoterapia a 15 L/min sob máscara, prevenir hipotermia com manta térmica e preparar transporte urgente.',
      perguntaTurma: 'Controlada a hemorragia com torniquete, quais são os parâmetros do XABCDE a serem reavaliados e qual a prioridade clínica?',
      orientacaoInstrutor: 'Vítima em choque hipovolêmico classe III no XABCDE (C: taquicardia 130 bpm, PA 90/60 mmHg, TEC 3 s). Prioridade Crítica (Vermelho). Instalar oxigenoterapia a 15 L/min sob máscara, prevenir hipotermia com manta térmica e preparar transporte urgente.',
      focoCamera: 'vitima',
      poseCamera: [-1.2, 1.2, 0.8],
      cartoes: [
        {
          id: 'c6_tri_choque',
          titulo: 'Choque Hipovolêmico Classe III',
          categoria: 'clinico',
          destaque: 'Prioridade Crítica (Vermelho)',
          conteudo: 'FC 130 bpm, PA 90/60 mmHg, pele fria e pálida, SpO2 92%, confusão mental moderada por hipoperfusão cerebral.',
          condutaRecomendada: 'Manta térmica, oxigênio de alto fluxo e preparação para transporte urgente ao bloco cirúrgico.',
        },
      ],
    },
    {
      id: 'extracao',
      numero: 6,
      nome: 'Decisão de Extração',
      subtitulo: 'Imobilização com Maca Tipo Concha (Scoop)',
      decisaoTatica: 'Por que a maca tipo concha (scoop stretcher) é o dispositivo indicado para transferência de vítima com suspeita de fratura pélvica/femoral?',
      diretrizOperacional: 'A maca tipo concha fecha-se em duas metades longitudinais sob o corpo do paciente sem necessidade de rolamento lateral de 90°, minimizando a dor, a hemorragia retroperitoneal e o deslocamento de fragmentos ósseos.',
      perguntaTurma: 'Por que a maca tipo concha (scoop stretcher) é o dispositivo indicado para transferência de vítima com suspeita de fratura pélvica/femoral?',
      orientacaoInstrutor: 'A maca tipo concha fecha-se em duas metades longitudinais sob o corpo do paciente sem necessidade de rolamento lateral de 90°, minimizando a dor, a hemorragia retroperitoneal e o deslocamento de fragmentos ósseos.',
      focoCamera: 'extracao',
      poseCamera: [-1.5, 2, 2],
      cartoes: [
        {
          id: 'c6_ext_concha',
          titulo: 'Maca Concha (Scoop) com Faixa Pélvica',
          categoria: 'extracao',
          destaque: 'Zero Movimentação da Pelve e Fêmur Fraturado',
          conteudo: 'Fechamento simétrico das lâminas sob a vítima no solo, aplicação de cinta pélvica de contenção e transferência rápida para a viatura.',
        },
      ],
    },
  ],
}

/**
 * Cenário 7: Barreira de Proteção Rodoviária (45° a 15m)
 */
const CENARIO_7_BARREIRA_PROTECAO: CenarioDidatico = {
  id: 'cenario-7',
  idPreset: 'cenario-7',
  titulo: 'Cenário 7: Barreira de Proteção Rodoviária (45° a 15m)',
  subtitulo: 'Bloqueio Tático ABT, Alerta Precoce, Cone de Transição e Corredor USA',
  descricao: 'Aplicação da doutrina de salvamento veicular em rodovias: caminhão de bombeiros em ângulo de 45° a 15 metros a montante, giroflex ativo, cone de transição de pista, área desobstruída para a USA e equipe com bombeiro e bombeiro médico.',
  objetivoOperacional: 'Garantir a integridade da guarnição e da vítima contra colisões secundárias por meio da massa física da viatura pesada a 45°, alerta luminoso estroboscópico precoce e corredor de evacuação para a USA.',
  badge: 'Segurança Rodoviária 45°',
  vitimas: [
    {
      id: 'cen7-vitima-condutor',
      nomeCurto: 'Condutor retido',
      papel: 'Motorista do veículo sinistrado em choque hipovolêmico',
      triagemReferencia: 'vermelho',
      local: 'veiculo',
      status: 'critica',
      parametrosXabcde: {
        xHemorragia: { valor: 'Hemorragia em coxa esquerda contida por pressão direta', critico: true },
        aViasAereas: { valor: 'Pérvia sob controle cervical manual inicial', conduta: 'Instalar colar cervical e oxigênio sob máscara' },
        bRespiracao: { fr: '26 irpm', spo2: '93% em ar ambiente', padrao: 'Taquipneico superficial com expansão preservada' },
        cCirculacao: { fc: '118 bpm', pa: '90/60 mmHg', tec: '3,5 s', estado: 'Sinais de choque hipovolêmico inicial' },
        dNeurologico: { glasgow: '14', pupilas: 'Isocóricas fotorreagentes', avdi: 'Voz (responde a comandos)' },
        eExposicao: { temp: '35,8 °C', achados: 'Deformidade de membro inferior esquerdo retido pelo painel' },
      },
    },
  ],
  criarCena: () => copiarCenaTabletop(PRESET_CENARIO_7_BARREIRA_PROTECAO),
  etapas: [
    {
      id: '360',
      numero: 1,
      nome: 'Dimensionamento da Cena & Barreira a 45°',
      subtitulo: 'Bloqueio Físico a 15m a Montante',
      decisaoTatica: 'Por que a viatura ABT deve ser posicionada a 45° e a 15 metros da zona de trabalho?',
      diretrizOperacional: 'A massa de 16 toneladas da viatura de bombeiros funciona como escudo contra colisões secundárias de veículos em alta velocidade. O ângulo de 45° com rodas esterçadas para fora da pista garante que, se atingida, a viatura seja defletida para o acostamento e não sobre a equipe de socorristas. A distância de 15m estabelece a zona de amortecimento.',
      perguntaTurma: 'Por que a viatura ABT deve ser posicionada a 45° e a 15 metros da zona de trabalho?',
      orientacaoInstrutor: 'A massa de 16 toneladas da viatura de bombeiros funciona como escudo contra colisões secundárias de veículos em alta velocidade. O ângulo de 45° com rodas esterçadas para fora da pista garante que, se atingida, a viatura seja defletida para o acostamento e não sobre a equipe de socorristas. A distância de 15m estabelece a zona de amortecimento.',
      focoCamera: 'panoramica',
      poseCamera: [0, 4.5, 9.5],
      cartoes: [
        {
          id: 'c7_360_angulo_45',
          titulo: 'Ângulo de 45° e Rodas Esterçadas',
          categoria: 'seguranca',
          destaque: 'Deflexão Mecânica Segura',
          conteudo: 'A viatura deve ser angulada a 45° em relação ao eixo da via, com o rodado dianteiro completamente virado em direção ao acostamento/fora da pista para evitar projeção para a zona quente.',
        },
        {
          id: 'c7_360_distancia_15m',
          titulo: 'Distância Crítica de 15 Metros',
          categoria: 'seguranca',
          destaque: 'Zona de Amortecimento de Impacto',
          conteudo: 'Os 15 metros entre o para-choque traseiro do caminhão e o veículo sinistrado absorvem o empurrão de uma eventual colisão traseira sem prensar a equipe.',
        },
      ],
    },
    {
      id: 'riscos',
      numero: 2,
      nome: 'Alerta Precoce & Cone de Transição',
      subtitulo: 'Sinalização Ativa e Desvio Suave',
      decisaoTatica: 'Como avisar precocemente os condutores que se aproximam e canalizar o fluxo da via?',
      diretrizOperacional: 'Manter a barra de giroflex e sinalizadores estroboscópicos 100% ligados. Montar o cone de transição com cones espaçados progressivamente desde 30m a montante até a viatura para forçar a desaceleração e desvio seguro.',
      perguntaTurma: 'Como avisar precocemente os condutores que se aproximam e canalizar o fluxo da via?',
      orientacaoInstrutor: 'Manter a barra de giroflex e sinalizadores estroboscópicos 100% ligados. Montar o cone de transição com cones espaçados progressivamente desde 30m a montante até a viatura para forçar a desaceleração e desvio seguro.',
      focoCamera: 'riscos',
      poseCamera: [0, 3.8, -18],
      cartoes: [
        {
          id: 'c7_ris_giroflex',
          titulo: 'Luzes de Emergência em Modo Alerta',
          categoria: 'risco',
          destaque: 'Visibilidade a Mais de 500 Metros',
          conteudo: 'O giroflex estroboscópico de LEDs de alta intensidade garante visibilidade imediata mesmo sob sol forte ou chuva.',
        },
        {
          id: 'c7_ris_cone_transicao',
          titulo: 'Cone de Transição em Afunilamento',
          categoria: 'seguranca',
          destaque: 'Desvio Gradual da Pista',
          conteudo: 'O afunilamento com cones conduz os veículos da pista bloqueada diretamente para a faixa liberada sem frenagens bruscas.',
        },
      ],
    },
    {
      id: 'cinematica',
      numero: 3,
      nome: 'Cinemática do Trauma & Energia de Rodovia',
      subtitulo: 'Impacto em Alta Velocidade e Risco Residual',
      decisaoTatica: 'Quais forças cinéticas atuaram sobre o condutor e quais riscos de colisão secundária persistem na via?',
      diretrizOperacional: 'A desaceleração em rodovia transfere alta energia cinética para o assoalho e painel, provocando compressão mecânica de extremidades. A proteção da barreira a 45° é vital para mitigar a energia de outros veículos que trafegam na rodovia.',
      perguntaTurma: 'Quais forças cinéticas atuaram sobre o condutor e quais riscos de colisão secundária persistem na via?',
      orientacaoInstrutor: 'A desaceleração em rodovia transfere alta energia cinética para o assoalho e painel, provocando compressão mecânica de extremidades. A proteção da barreira a 45° é vital para mitigar a energia de outros veículos que trafegam na rodovia.',
      focoCamera: 'cinematica',
      poseCamera: [0, 2.8, 3.5],
      cartoes: [
        {
          id: 'c7_cin_desaceleracao',
          titulo: 'Desaceleração Brusca na Pista',
          categoria: 'cinematica',
          destaque: 'Impacto Longitudinal em Rodovia',
          conteudo: 'A deformação frontal comprimiu a pedaleira contra os membros inferiores, exigindo tração ou corte do painel para livramento.',
        },
      ],
    },
    {
      id: 'abordagem',
      numero: 4,
      nome: 'Corredor Livre da USA & Aproximação',
      subtitulo: 'Reserva Tática de Evacuação Rápida',
      decisaoTatica: 'Onde posicionar o espaço reservado para a USA e qual o papel dos socorristas?',
      diretrizOperacional: 'O acostamento/lateral protegida deve ser demarcado como Área Livre da USA, proibindo o depósito de ferramentas ou veículos secundários. O bombeiro de salvamento estabiliza a cena e o bombeiro médico aproxima-se com oxigênio portátil e bolsa de primeiros socorros.',
      perguntaTurma: 'Onde posicionar o espaço reservado para a USA e qual o papel dos socorristas?',
      orientacaoInstrutor: 'O acostamento/lateral protegida deve ser demarcado como Área Livre da USA, proibindo o depósito de ferramentas ou veículos secundários. O bombeiro de salvamento estabiliza a cena e o bombeiro médico aproxima-se com oxigênio portátil e bolsa de primeiros socorros.',
      focoCamera: 'vitima',
      poseCamera: [-1.5, 2.0, 1.0],
      cartoes: [
        {
          id: 'c7_abo_corredor_usa',
          titulo: 'Corredor Exclusivo da USA',
          categoria: 'seguranca',
          destaque: 'Acesso Direto para UTI Móvel',
          conteudo: 'Garante que a ambulância do SAMU/USA chegue, receba a vítima e parta em direção ao hospital sem ficar encurralada por cones ou viaturas.',
        },
        {
          id: 'c7_abo_kit_o2_trauma',
          titulo: 'Aporte de O2 e Kit de Trauma APH',
          categoria: 'clinico',
          destaque: 'Oxigenoterapia e Controle Imediato',
          conteudo: 'O bombeiro médico posiciona o cilindro verde de O2 a 12-15 L/min sob máscara com reservatório e kit de hemostasia rápida.',
        },
      ],
    },
    {
      id: 'triagem_xabcde',
      numero: 5,
      nome: 'Exame Primário XABCDE em Zona Segura',
      subtitulo: 'Prioridade à Vítima Crítica',
      decisaoTatica: 'Qual a prioridade no exame clínico do condutor retido?',
      diretrizOperacional: 'Identificar choque hipovolêmico classe II/III e contenção de hemorragia em membro inferior. Colar cervical instalado e preparo para desencarceramento rápido.',
      perguntaTurma: 'Qual a prioridade no exame clínico do condutor retido?',
      orientacaoInstrutor: 'Identificar choque hipovolêmico classe II/III e contenção de hemorragia em membro inferior. Colar cervical instalado e preparo para desencarceramento rápido.',
      focoCamera: 'vitima',
      poseCamera: [-0.8, 1.6, 0.5],
      cartoes: [
        {
          id: 'c7_tri_xabcde',
          titulo: 'Protocolo XABCDE Tático',
          categoria: 'clinico',
          destaque: 'Avaliação sob Cobertura Física',
          conteudo: 'Trabalho tranquilo e concentrado da equipe médica e de salvamento graças à blindagem da barreira a 45°.',
        },
      ],
    },
    {
      id: 'extracao',
      numero: 6,
      nome: 'Extração Tática e Transferência',
      subtitulo: 'Retirada Direta ao Corredor da USA',
      decisaoTatica: 'Qual rota de extração assegura transferência imediata para a USA?',
      diretrizOperacional: 'Retirada a 0° ou lateral através da porta aberta, posicionamento em prancha rígida e transporte direto pelos socorristas até a ambulância no corredor livre.',
      perguntaTurma: 'Qual rota de extração assegura transferência imediata para a USA?',
      orientacaoInstrutor: 'Retirada a 0° ou lateral através da porta aberta, posicionamento em prancha rígida e transporte direto pelos socorristas até a ambulância no corredor livre.',
      focoCamera: 'extracao',
      poseCamera: [0, 3.0, 2.5],
      cartoes: [
        {
          id: 'c7_ext_rota_usa',
          titulo: 'Transferência Rápida ao Corredor Livre',
          categoria: 'extracao',
          destaque: 'Tempo de Cena Reduzido',
          conteudo: 'A vítima é embarcada diretamente na maca da USA sem cruzamento perigoso de pistas de rolamento.',
        },
      ],
    },
  ],
}

export const CENARIOS_DIDATICOS: readonly CenarioDidatico[] = [
  CENARIO_1_COLISAO_MURO,
  CENARIO_2_COLISAO_LATERAL,
  CENARIO_3_CAPOTAMENTO,
  CENARIO_4_CARRO_SOBRE_OUTRO,
  CENARIO_5_DUAS_ESTAVEIS,
  CENARIO_6_ATROPELAMENTO,
  CENARIO_7_BARREIRA_PROTECAO,
]

export const obterCenarioDidaticoPorId = (id: string): CenarioDidatico | undefined => {
  const mapaLegado: Record<string, string> = {
    'slide-49-50': 'cenario-1',
    'slide-51-52': 'cenario-2',
    'capotamento-ejetada': 'cenario-3',
    'complexo': 'cenario-4',
    'carro-sobre-outro': 'cenario-4',
    'slide-53-54': 'cenario-5',
    'atropelamento-hemorragia': 'cenario-6',
  }
  const idNormalizado = mapaLegado[id] ?? id
  return CENARIOS_DIDATICOS.find((c) => c.id === idNormalizado)
}
